import React, { FC, RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { OrderStateBadge } from '@components/molecules/badges/OrderStateBadge';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { formatDate, formatPrice } from '@utils/format';
import { OrderState, ResultsType } from '@types';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useGetMyOrders } from '@services/useGetMyOrders';
import { useData } from '@hooks/useData';
import { useFilters } from '@hooks/useFilters';
import { Form } from '@components/atoms/Form';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { Pagination } from '@components/molecules/Pagination';
import { SITE_URLS } from '@constants';

const TEXT_FILTER_ID = 'text-filter';

export const MyOrders: FC = () => {
    const { pageToRequest, checkPageToRequest, observerRef: scrollObserverRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('my-orders-component.title'),
        searchPlaceholder: t('my-orders-component.search.placeholder'),
    };

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4 pb-2">
                    <Filters formRef={formRef} pageToRequest={pageToRequest} checkPageToRequest={checkPageToRequest} />
                </div>
                <div className="pt-4">
                    <OrdersTable />
                    <div ref={scrollObserverRef} style={{ height: '1px' }} />
                </div>
            </>
        </MyAccountContainer>
    );
};

interface FiltersProps {
    formRef: RefObject<HTMLFormElement>;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

const Filters: FC<FiltersProps> = ({ formRef, pageToRequest, checkPageToRequest }) => {
    const { request: requestMyOrders } = useGetMyOrders();
    const { myOrders, isLoadingMyOrders, setMyOrders } = useData();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage } = useFilters();

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setMyOrders(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTextFilter = appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name;

    const fetchOrders = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (myOrders?.pagination?.total_pages && page > myOrders?.pagination?.total_pages) {
            return;
        }

        requestMyOrders({
            textSearch: getTextFilter,
            page: page,
        });
    };

    // request more orders from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingMyOrders && fetchOrders(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest, isLoadingMyOrders]);

    // request first page whenever a filter changes
    const submitForm = () => fetchOrders(1);

    useEffect(() => {
        !isLoadingMyOrders && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setResults((previous: any[]) => {
            return myOrders?.pagination?.current_page === 1 ? myOrders?.data || [] : [...previous, ...(myOrders?.data || [])];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myOrders]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <MyAccountContainer.Filters>
            <Form formRef={formRef} onSubmit={submitForm}>
                <TextFilter />
            </Form>
        </MyAccountContainer.Filters>
    );
};

const OrdersTable: FC = () => {
    const pageSize = useMediaQuery();
    const { results: orders } = useFilters();
    const { isLoadingMyOrders } = useData();
    const { t } = useTranslation();

    const translations = {
        idShort: t('my-orders-management-table-component.id-short'),
        idLong: t('my-orders-management-table-component.id-long'),
        date: t('my-orders-management-table-component.date'),
        nProducts: t('my-orders-management-table-component.n-products'),
        state: t('my-orders-management-table-component.state'),
        total: t('my-orders-management-table-component.total'),
        actions: t('my-orders-management-table-component.actions'),
    };

    return (
        <div data-testid="management-table">
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        {
                            classes: 'text-left',
                            text: ['xs', 'sm'].includes(pageSize) ? translations.idShort : translations.idLong,
                        },
                        { classes: 'text-center', hideOnMobile: true, text: translations.date },
                        { classes: 'text-center', hideOnMobile: true, text: translations.nProducts },
                        { classes: 'text-center', text: translations.state },
                        { classes: 'text-end', hideOnMobile: true, text: translations.total },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={orders.map((order) => {
                        const commonOptions = [
                            <Anchor
                                key={`view-${order.uuid}`}
                                href={SITE_URLS.myOrderDetail.replace(':uuid', `${order.uuid}`)}
                                mode={AnchorMode.none}>
                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                    <i className="fa-light fa-eye" />
                                </Typography>
                            </Anchor>,
                        ];

                        if (order.status === 'delivered') {
                            commonOptions.push(
                                <Anchor
                                    key={`complain-${order.uuid}`}
                                    href={SITE_URLS.complaint.replace(':uuid', `${order.uuid}`)}
                                    mode={AnchorMode.none}>
                                    <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                        <i className="fa-light fa-exclamation-triangle" />
                                    </Typography>
                                </Anchor>
                            );
                        }

                        return [
                            {
                                classes: 'text-left text-muted',
                                content: <Typography fontSize="14">{order.order_code}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatDate(order.date)}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{`${order.nProducts}`}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <OrderStateBadge state={OrderState[order.status as keyof typeof OrderState]} />,
                            },
                            {
                                classes: 'text-end',
                                content: <Typography fontSize="14">{formatPrice(order.total)}</Typography>,
                            },
                            {
                                classes: 'text-end',
                                content: <Table.Options options={commonOptions} />,
                            },
                        ];
                    })}
                />
            </Card>
            <Pagination loading={isLoadingMyOrders} type={ResultsType.orders} />
        </div>
    );
};

const TextFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        searchPlaceholder: t('my-orders-component.search.placeholder'),
    };

    return (
        <div className="align-content-center" style={{ width: ['xs', 'sm', 'md'].includes(pageSize) ? '100%' : 'unset' }}>
            <Input
                type={InputTypes.text}
                variant={InputVariants.searchInput}
                placeholder={translations.searchPlaceholder}
                value={appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name || ''}
                onChange={(value) => setFilterValues(TEXT_FILTER_ID, [{ uuid: null, name: value }])}
            />
        </div>
    );
};
